// src/modules/operaciones/inventarios/utils/informesComun.js
//
// Utilidades compartidas del módulo de Informes: formateo de números, moneda, fechas y
// paletas de color para el dashboard.
//
// COMPATIBILIDAD: se mantienen los exports que ya existían
//   (formatoComun, etiquetaTipo, fmtNumero, fmtFecha y el export por defecto).
//
// NOTA SOBRE EL FORMATO NUMÉRICO
// Se formatea "a mano" (punto para miles, coma para decimales, como en es-CO) en lugar de
// depender de toLocaleString, porque en algunos navegadores/ICU el locale "es-CO" no agrupa
// los números de 4 dígitos (1234 en vez de 1.234) y el resultado cambiaría entre equipos.

export const formatoComun = {};

/* ========================================================================== */
/* Núcleo de formateo numérico                                                */
/* ========================================================================== */

// Devuelve { negativo, texto } con el número ya redondeado y agrupado, o null si no es finito.
// - minDec / maxDec: decimales mínimos y máximos (los ceros sobrantes se recortan hasta minDec).
// - Un valor que redondea a 0 nunca se marca como negativo (evita "-0,00").
function partesNumero(valor, minDec = 0, maxDec = 0) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return null;

  const abs = Math.abs(n);
  if (abs >= 1e21) return { negativo: n < 0, texto: String(abs) };

  const fijo = abs.toFixed(maxDec);
  const negativo = n < 0 && Number(fijo) !== 0;
  let [entero, decimal = ""] = fijo.split(".");

  decimal = decimal.replace(/0+$/, "");
  while (decimal.length < minDec) decimal += "0";

  entero = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return { negativo, texto: decimal ? `${entero},${decimal}` : entero };
}

const SIN_DATO = "-";

/* ========================================================================== */
/* Funciones existentes (compatibilidad)                                      */
/* ========================================================================== */

export const etiquetaTipo = (tipo) => {
  return tipo ? String(tipo).toUpperCase() : "DESCONOCIDO";
};

// Número con separador de miles y hasta 3 decimales (igual que el comportamiento anterior).
// Un valor vacío o no numérico se muestra como 0.
export const fmtNumero = (numero, decimales = null) => {
  const n = Number(numero || 0);
  const p =
    decimales === null || decimales === undefined
      ? partesNumero(n, 0, 3)
      : partesNumero(n, decimales, decimales);
  if (!p) return "0";
  return p.negativo ? `-${p.texto}` : p.texto;
};

export const fmtFecha = (fecha) => {
  if (!fecha) return "-";
  try {
    // "2024-05-03" (solo fecha) se interpretaría como UTC y en Colombia (UTC-5) mostraría
    // el día anterior; se construye como fecha local para evitarlo.
    const m = typeof fecha === "string" ? fecha.match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(fecha);
    if (Number.isNaN(d.getTime())) return String(fecha);
    return d.toLocaleDateString("es-CO");
  } catch (e) {
    return fecha;
  }
};

/* ========================================================================== */
/* Moneda, compacto y porcentaje                                              */
/* ========================================================================== */

// Moneda COP sin decimales por defecto.
//   fmtMoneda(10466026414)         → "$10.466.026.414"
//   fmtMoneda(-63663.84)           → "-$63.664"
//   fmtMoneda(1500.5, { decimales: 2 })  → "$1.500,50"
//   fmtMoneda(250, { signo: true })      → "+$250"   (útil para ganancias/sobrantes)
export const fmtMoneda = (valor, { decimales = 0, signo = false } = {}) => {
  if (valor === null || valor === undefined || valor === "") return SIN_DATO;
  const p = partesNumero(valor, decimales, decimales);
  if (!p) return SIN_DATO;
  if (p.negativo) return `-$${p.texto}`;
  const esCero = Number(String(p.texto).replace(/\./g, "").replace(",", ".")) === 0;
  return signo && !esCero ? `+$${p.texto}` : `$${p.texto}`;
};

// Escalas en español: 1.000 = "mil", 1.000.000 = "M", 1.000.000.000 = "mil M", 1e12 = "B".
const ESCALAS = [
  { divisor: 1e12, sufijo: "B" },
  { divisor: 1e9, sufijo: "mil M" },
  { divisor: 1e6, sufijo: "M" },
  { divisor: 1e3, sufijo: "mil" },
];

// Formato compacto para tarjetas KPI y ejes de gráficos.
//   fmtCompacto(10466026414)  → "$10,5 mil M"
//   fmtCompacto(-11398214)    → "-$11,4 M"
//   fmtCompacto(350000)       → "$350 mil"
//   fmtCompacto(999950)       → "$1 M"        (si al redondear llega a 1000, sube de escala)
//   fmtCompacto(4200, { moneda: false }) → "4,2 mil"
export const fmtCompacto = (valor, { moneda = true, decimales = 1 } = {}) => {
  if (valor === null || valor === undefined || valor === "") return SIN_DATO;
  const n = Number(valor);
  if (!Number.isFinite(n)) return SIN_DATO;

  const prefijo = moneda ? "$" : "";
  const abs = Math.abs(n);

  // Menor a mil: se muestra tal cual (con decimales si los trae)
  if (abs < 1e3) {
    const p = partesNumero(n, 0, decimales);
    return `${p.negativo ? "-" : ""}${prefijo}${p.texto}`;
  }

  // Elige la escala más grande que no supere el valor
  let idx = ESCALAS.findIndex((e) => abs >= e.divisor);
  let escalado = abs / ESCALAS[idx].divisor;

  // Si al redondear llega a 1000 (ej. 999,96 M), sube a la escala siguiente
  while (idx > 0 && Number(escalado.toFixed(decimales)) >= 1000) {
    idx -= 1;
    escalado = abs / ESCALAS[idx].divisor;
  }

  const p = partesNumero(escalado, 0, decimales);
  const negativo = n < 0 && Number(escalado.toFixed(decimales)) !== 0;
  return `${negativo ? "-" : ""}${prefijo}${p.texto} ${ESCALAS[idx].sufijo}`;
};

// Porcentaje con coma decimal.
// Por defecto recibe el valor YA en porcentaje (−0,109 → "-0,11%").
//   fmtPorcentaje(-0.109)                         → "-0,11%"
//   fmtPorcentaje(0.0167, 2, { ratio: true })     → "1,67%"   (0,0167 = 1,67 %)
//   fmtPorcentaje(12.345, 1, { signo: true })     → "+12,3%"
export const fmtPorcentaje = (valor, decimales = 2, { ratio = false, signo = false } = {}) => {
  if (valor === null || valor === undefined || valor === "") return SIN_DATO;
  const n = Number(valor);
  if (!Number.isFinite(n)) return SIN_DATO;
  const p = partesNumero(ratio ? n * 100 : n, decimales, decimales);
  if (!p) return SIN_DATO;
  const esCero = !p.negativo && Number(p.texto.replace(/\./g, "").replace(",", ".")) === 0;
  const prefijo = p.negativo ? "-" : signo && !esCero ? "+" : "";
  return `${prefijo}${p.texto}%`;
};

/* ========================================================================== */
/* Texto                                                                      */
/* ========================================================================== */

// Recorta un texto largo (nombres de artículos en ejes de gráficos) agregando "…".
// El resultado nunca supera `max` caracteres.
//   truncar("CONCENT FILPO x KILO AGRANEL ADULTO", 18) → "CONCENT FILPO x K…"
export const truncar = (texto, max = 24, sufijo = "…") => {
  if (texto === null || texto === undefined) return "";
  const t = String(texto).trim();
  if (max <= 0 || t.length <= max) return t;
  const corte = Math.max(max - sufijo.length, 1);
  return `${t.slice(0, corte).trimEnd()}${sufijo}`;
};

/* ========================================================================== */
/* Paletas de color                                                           */
/* ========================================================================== */

// Colores con significado fijo en todo el dashboard.
export const COLORES_SEMANTICOS = {
  perdida: "#ef4444",       // rojo: faltantes, mermas, valores negativos
  perdidaOscuro: "#b91c1c",
  perdidaClaro: "#fecaca",
  sobrante: "#10b981",      // verde: sobrantes, ganancias, valores positivos
  sobranteOscuro: "#047857",
  sobranteClaro: "#a7f3d0",
  venta: "#3b82f6",         // azul: ventas
  ventaOscuro: "#1d4ed8",
  ventaClaro: "#bfdbfe",
  conocida: "#f97316",      // naranja: merma conocida
  desconocida: "#8b5cf6",   // violeta: merma desconocida
  entrada: "#06b6d4",       // cian: movimientos de entrada
  salida: "#ec4899",        // rosa: movimientos de salida
  alerta: "#f59e0b",        // ámbar: advertencias
  neutro: "#94a3b8",        // gris: sin dato / otros
  neutroClaro: "#e2e8f0",
  texto: "#6b7280",
  grilla: "#f3f4f6",
};

// Degradados de un mismo color (de intenso a suave), útiles para rankings y treemaps.
export const PALETA_PERDIDA = ["#b91c1c", "#dc2626", "#ef4444", "#f87171", "#fca5a5", "#fecaca"];
export const PALETA_SOBRANTE = ["#047857", "#059669", "#10b981", "#34d399", "#6ee7b7", "#a7f3d0"];
export const PALETA_VENTA = ["#1d4ed8", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe"];

// Categorías (fabricantes, familias, bodegas...). Sin rojo ni verde para no confundirse con los semánticos.
export const PALETA_CATEGORIAS = [
  "#3b82f6", "#8b5cf6", "#06b6d4", "#f59e0b", "#ec4899",
  "#14b8a6", "#6366f1", "#f97316", "#84cc16", "#0ea5e9",
];

// Color para la porción "Otros" en donas y treemaps.
export const COLOR_OTROS = "#cbd5e1";

// Mapa de calor divergente: rojo (negativo) ← blanco (cero) → verde (positivo).
export const PALETA_DIVERGENTE = ["#b91c1c", "#ef4444", "#fca5a5", "#f8fafc", "#6ee7b7", "#10b981", "#047857"];

// Color de una categoría por posición (se repite cíclicamente).
export const colorCategoria = (indice) =>
  PALETA_CATEGORIAS[((Number(indice) || 0) % PALETA_CATEGORIAS.length + PALETA_CATEGORIAS.length) % PALETA_CATEGORIAS.length];

// Rojo si es negativo, verde si es positivo y gris si es cero o no es número.
export const colorPorSigno = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n) || n === 0) return COLORES_SEMANTICOS.neutro;
  return n < 0 ? COLORES_SEMANTICOS.perdida : COLORES_SEMANTICOS.sobrante;
};

const hexARgb = (hex) => {
  const h = String(hex).replace("#", "");
  const completo = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [0, 2, 4].map((i) => parseInt(completo.slice(i, i + 2), 16));
};

// Mezcla dos colores hex. t = 0 → colorA, t = 1 → colorB. Devuelve "rgb(r, g, b)".
export const mezclarColores = (colorA, colorB, t) => {
  const k = Math.min(Math.max(Number(t) || 0, 0), 1);
  const a = hexARgb(colorA);
  const b = hexARgb(colorB);
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * k));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
};

// Color de celda para mapas de calor: intensidad proporcional a |valor| / maxAbs.
//   colorDivergente(-5_000_000, 20_000_000) → rojo suave
//   colorDivergente(0, 20_000_000)          → casi blanco
export const colorDivergente = (valor, maxAbs) => {
  const n = Number(valor);
  const max = Math.abs(Number(maxAbs));
  if (!Number.isFinite(n) || !Number.isFinite(max) || max === 0 || n === 0) return "#f8fafc";
  const t = Math.min(Math.abs(n) / max, 1);
  return mezclarColores("#f8fafc", n < 0 ? COLORES_SEMANTICOS.perdida : COLORES_SEMANTICOS.sobrante, t);
};

/* ========================================================================== */
/* Export por defecto (compatibilidad)                                        */
/* ========================================================================== */

Object.assign(formatoComun, {
  etiquetaTipo,
  fmtNumero,
  fmtFecha,
  fmtMoneda,
  fmtCompacto,
  fmtPorcentaje,
  truncar,
  COLORES_SEMANTICOS,
  PALETA_CATEGORIAS,
});

export default formatoComun;